import { useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";

import { showToast } from "utils/helpers";

import { RBAC_STRINGS } from "../../constants/rbacStrings";
import { useRevokeMember } from "../../hooks/usePanelScoringQueries";
import { useGuestActionBlock } from "../../hooks/useGuestActionBlock";
import { deriveEventGrantDisplayState } from "../../utils/eventGrantDisplayState";
import type {
	PanelGrantRow,
	ResourceCapabilityResource,
} from "../../types/eventGrant.type";
import { MemberReasonModal } from "../PanelMembers/MemberReasonModal";
import { CoiResolutionModal } from "./CoiResolutionModal";
import { EventGrantStatusBadge } from "./EventGrantStatusBadge";
import { GuestUpgradeCta } from "./GuestUpgradeCta";

/**
 * Panel/grant member list that surfaces each row's server-authoritative
 * display state (pending / active / released / read-only / suspended /
 * flagged / revoked — AC3) and gates mutating affordances on the resolved
 * resource capability (FE-3/H1):
 *
 *  - Post-close (capability.read_only) → NO mutating affordances; released
 *    rows carry a distinct "released to read-only" badge (AC3).
 *  - Guest → controls disabled + upgrade CTA, never hidden (FE-4).
 *  - RFx Lead revoke removes access and the list reflects it immediately (AC6).
 *  - A COI-flagged/suspended row exposes Resolve, gated `can:resolveCoi`.
 */

interface PanelMemberListProps {
	rfxId: number;
	evalId: number;
	members: PanelGrantRow[];
	/** Server-authoritative capability for the panel context (§3, Task 011). */
	capability?: ResourceCapabilityResource;
	/** True once the evaluation has closed (drives auto-release badge, AC3). */
	evaluationClosed?: boolean;
	/** Starts the guest company-upgrade flow (VT-027). */
	onUpgrade?: () => void;
}

const COI_STATES = new Set(["suspended", "flagged"]);

export const PanelMemberList = ({
	rfxId,
	evalId,
	members,
	capability,
	evaluationClosed = false,
	onUpgrade,
}: PanelMemberListProps) => {
	const { isGuest } = useGuestActionBlock();
	// The VIEWER's capability read-only gates THIS caller's affordances and the
	// list-level notice only — it is per-caller and is NOT used to derive any
	// member's per-row badge (review F1).
	const readOnly = capability?.read_only ?? false;
	// COI-resolve gate sourced from the server-authoritative capability flag
	// (FF-W3-1) — single-homed with the route's `can:resolveCoi`, not a
	// client-derived role check.
	const canResolveCoi = capability?.can.resolveCoi ?? false;

	const revokeMutation = useRevokeMember(rfxId, evalId);
	const [revokeTarget, setRevokeTarget] = useState<PanelGrantRow | null>(null);
	const [coiTarget, setCoiTarget] = useState<PanelGrantRow | null>(null);

	const handleConfirmRevoke = (reason: string): void => {
		if (!revokeTarget) {
			return;
		}
		revokeMutation.mutate(
			{ memberId: revokeTarget.id, reason },
			{
				onSuccess: () => {
					showToast("Member access has been revoked.", "Access Revoked", true);
					setRevokeTarget(null);
				},
				onError: () =>
					showToast("Failed to revoke access.", "Error", false),
			}
		);
	};

	return (
		<div data-testid="panel-member-list">
			{readOnly && (
				<div
					className="mb-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm text-gray-600"
					data-testid="panel-list-readonly-notice"
				>
					<FiAlertTriangle size={14} aria-hidden="true" />
					{evaluationClosed
						? RBAC_STRINGS.state.released
						: RBAC_STRINGS.state.readOnly}
				</div>
			)}

			<ul className="divide-y divide-gray-100">
				{members.map((member) => {
					const state = deriveEventGrantDisplayState({
						status: member.status,
						coi: member.coi,
						isEventGrant: member.is_event_grant,
						evaluationClosed,
					});
					const isCoi = COI_STATES.has(state);
					// Post-close/read-only viewers get NO mutating affordances
					// (AC3 — hidden is fine). Guests DO see the controls, but
					// disabled + upgrade CTA (FE-4 — never hidden, review F2).
					const revokableStatus =
						member.status === "accepted" ||
						member.status === "invited" ||
						member.status === "pending";
					const showRevoke = !readOnly && revokableStatus;
					// Resolve targets a panel-member id; never offered without
					// one (review F4 — no fall back to the row id).
					const hasCoiTarget = member.panel_member_id != null;
					const showResolve =
						!readOnly && isCoi && canResolveCoi && hasCoiTarget;

					return (
						<li
							key={member.id}
							className="flex items-center justify-between gap-3 py-3"
							data-testid={`panel-member-row-${member.id}`}
						>
							<div className="min-w-0">
								<p className="truncate text-sm font-medium text-gray-800">
									{member.name || member.email}
								</p>
								<p className="truncate text-xs text-gray-500">
									{member.email} · {member.event_role}
								</p>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								<EventGrantStatusBadge state={state} />
								{showResolve && (
									<button
										type="button"
										onClick={() => setCoiTarget(member)}
										disabled={isGuest}
										aria-disabled={isGuest}
										className="rounded px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
										data-testid={`panel-member-resolve-coi-${member.id}`}
									>
										Resolve
									</button>
								)}
								{showRevoke && (
									<button
										type="button"
										onClick={() => setRevokeTarget(member)}
										disabled={isGuest}
										aria-disabled={isGuest}
										className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
										data-testid={`panel-member-revoke-${member.id}`}
									>
										Revoke
									</button>
								)}
							</div>
						</li>
					);
				})}
			</ul>

			{isGuest && (
				<GuestUpgradeCta className="mt-3" onUpgrade={onUpgrade} />
			)}

			<MemberReasonModal
				isOpen={revokeTarget !== null}
				title="Revoke Member Access"
				confirmLabel="Revoke Access"
				reasonLabel="Reason for revoking access"
				isSubmitting={revokeMutation.isPending}
				onConfirm={handleConfirmRevoke}
				onClose={() => setRevokeTarget(null)}
				body={
					<p className="text-sm text-gray-600">
						Are you sure you want to revoke access for{" "}
						<strong>{revokeTarget?.name || revokeTarget?.email}</strong>? Their
						access is removed immediately and any entered scores are excluded.
					</p>
				}
			/>

			{coiTarget?.panel_member_id != null && (
				<CoiResolutionModal
					isOpen
					rfxId={rfxId}
					evalId={evalId}
					memberId={coiTarget.panel_member_id}
					memberName={coiTarget.name || coiTarget.email}
					coi={coiTarget.coi}
					canResolve={canResolveCoi}
					onClose={() => setCoiTarget(null)}
					onResolved={() => setCoiTarget(null)}
				/>
			)}
		</div>
	);
};
